import { updateReflectionMap } from "@project/emerge/src/editor3d/ReflectionMap";
import { StandardEnvironment } from "@project/emerge/src/new/StandardEnvironment";
import { Grid } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import type { CubeTexture } from "three";

/**
 * Scene that displays a grid on the floor with the standard environment
 */
export const GridFloorScene = () => {
  const { scene } = useThree();
  const envMapRef = useRef<CubeTexture | null>(null);

  // Handle environment creation
  const handleEnvironmentCreated = (envMap: CubeTexture) => {
    // Store for later reference
    envMapRef.current = envMap;

    // Set global scene environment
    scene.environment = envMap;

    // Update reflection map for compatibility with older materials
    updateReflectionMap(envMap);
  };

  // Clean up when unmounted
  useEffect(() => {
    return () => {
      if (envMapRef.current) {
        scene.environment = null;
      }
    };
  }, [scene]);

  return (
    <>
      {/* StandardEnvironment with custom intensity */}
      <StandardEnvironment
        resolution={512}
        isGlobal={true}
        {...{ onCreated: handleEnvironmentCreated }}
      />

      {/* Grid on the floor - positioned to align with scaled products */}
      <Grid
        position={[0, 0, 0]}
        args={[10, 10]} // Width and height of the grid
        cellSize={0.01} // Size of each cell (10x kleiner: 0.25 -> 0.025)
        cellThickness={0.5} // Thickness of the grid lines (hellgraue feine Linien)
        cellColor="#ccc" // Color of the grid lines (hellgrau für feine Unterteilung)
        sectionSize={0.1} // Size of each section (mittlere Linien alle 0.25)
        sectionThickness={0.6} // Thickness of the section lines
        sectionColor="#999" // Color of the section lines (mittelgrau)
        fadeDistance={20} // Distance at which the grid begins to fade
        infiniteGrid={true} // Whether the grid extends infinitely
        fadeStrength={1.5} // Strength of the fade effect
      />

      {/* Hauptgrid für grobe Orientierung */}
      <Grid
        position={[0, 0, 0]}
        args={[10, 10]} // Width and height of the grid
        cellSize={1} // Hauptraster alle 1 Einheit
        cellThickness={1.2} // Dickere Linien für Hauptraster
        cellColor="#666" // Dunkelgrau für Hauptlinien
        sectionSize={0} // Keine Sections
        fadeDistance={20} // Distance at which the grid begins to fade
        infiniteGrid={true} // Whether the grid extends infinitely
        fadeStrength={1.5} // Strength of the fade effect
      />
    </>
  );
};
